/**
 * SorteoOpinionForm - Formulario de opinión para el sorteo
 * Refactorizado para usar componentes reutilizables de OpinionFields
 */
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { submitOpinion, checkDuplicateOpinion } from "../../core/api/api";
import {
    RadioGroup,
    ScaleField,
    TextField,
    TextAreaField,
    CheckboxField,
} from "../opinions/OpinionFields";

// Botón neón para el formulario
const NeonButton = ({ children, onClick, type = "button", disabled = false }) => (
    <motion.button
        type={type}
        onClick={onClick}
        disabled={disabled}
        className="px-8 py-3 font-bold text-lg rounded-lg bg-gradient-to-r from-yellow-500 to-orange-500 text-gray-900 shadow-lg hover:shadow-yellow-500/50 transition-all duration-300 disabled:opacity-50"
        whileHover={{ scale: 1.05, boxShadow: "0 0 30px rgba(250, 204, 21, 0.5)" }}
        whileTap={{ scale: 0.95 }}
    >
        {children}
    </motion.button>
);

const SecondaryButton = ({ children, onClick }) => (
    <motion.button
        type="button"
        onClick={onClick}
        className="px-8 py-3 font-bold text-lg rounded-lg bg-transparent border-2 border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10 transition-all duration-300"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
    >
        {children}
    </motion.button>
);

// Estilos comunes para el formulario del sorteo (tema oscuro)
const darkThemeStyles = {
    labelClassName: "text-left block font-semibold text-yellow-400",
    optionClassName: "text-white",
    inputClassName: "border border-white/20 rounded-md p-2 w-full bg-white/10 text-white placeholder-white/50",
    descriptionClassName: "text-sm text-white/60 mb-2",
};

const SorteoOpinionForm = ({ onBack, onSuccess }) => {
    const { t } = useTranslation();
    const {
        register,
        handleSubmit,
        formState: { errors },
        watch,
        setValue,
    } = useForm();

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [duplicateError, setDuplicateError] = useState(null);

    // Handlers para campos numéricos
    const edadValue = watch("edad") || "";
    const cpValue = watch("cp") || "";

    const handleEdadChange = (e) => {
        const { value } = e.target;
        if (/^\d{0,3}$/.test(value)) setValue("edad", value);
    };

    const handleCpChange = (e) => {
        const { value } = e.target;
        if (/^\d{0,5}$/.test(value)) setValue("cp", value);
    };

    const validateUniversityEmail = (email) => {
        return /^[\w.-]+@alumnos\.uemc\.es$/i.test(email) || t("sorteoForm.emailInvalid");
    };

    const onSubmit = async (data) => {
        setIsSubmitting(true);
        setDuplicateError(null);
        try {
            const isDuplicate = await checkDuplicateOpinion(data.email, data.capitulo);
            if (isDuplicate) {
                setDuplicateError(t("sorteoForm.duplicateError", { chapter: data.capitulo }));
                setIsSubmitting(false);
                return;
            }
            await submitOpinion(data, true);
            onSuccess();
        } catch {
            alert(t("sorteoForm.submitError"));
        } finally {
            setIsSubmitting(false);
        }
    };

    // Opciones para campos radio
    const genderOptions = [
        { value: "Hombre", label: t("sorteoForm.genderMale") },
        { value: "Mujer", label: t("sorteoForm.genderFemale") },
        { value: "Otro", label: t("sorteoForm.genderOther") },
    ];

    const formatOptions = [
        { value: "Digital", label: t("sorteoForm.formatDigital") },
        { value: "Impreso", label: t("sorteoForm.formatPrinted") },
    ];

    const yesNoOptions = [
        { value: "Si", label: t("sorteoForm.yes") },
        { value: "No", label: t("sorteoForm.no") },
    ];

    const readingTimeOptions = [
        { value: "Corto", label: t("sorteoForm.short") },
        { value: "Adecuado", label: t("sorteoForm.adequate") },
        { value: "Largo", label: t("sorteoForm.long") },
    ];

    const preferredFormatOptions = [
        { value: "Digital", label: t("sorteoForm.formatDigital") },
        { value: "Impreso", label: t("sorteoForm.formatPrinted") },
        { value: "Ambos", label: t("sorteoForm.both") },
        { value: "NSNC", label: t("sorteoForm.noAnswer") },
    ];

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex items-center justify-center min-h-full"
        >
            <div className="backdrop-blur-md bg-white/10 p-6 rounded-lg shadow-lg w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col">
                <h2 className="text-2xl font-bold mb-4 text-yellow-400 text-center">
                    {t("sorteoForm.title")}
                </h2>

                {duplicateError && (
                    <div className="bg-red-500/20 rounded p-3 mb-4 text-center text-red-300">
                        {duplicateError}
                    </div>
                )}

                <div className="overflow-y-auto flex-1 pr-2">
                    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col gap-4">

                        {/* Email universitario */}
                        <TextField
                            label={t("sorteoForm.emailLabel")}
                            name="email"
                            type="email"
                            register={register}
                            validation={{ required: t("sorteoForm.emailRequired"), validate: validateUniversityEmail }}
                            error={errors.email}
                            errorMessage={errors.email?.message}
                            placeholder={t("sorteoForm.emailPlaceholder")}
                            helpText={t("sorteoForm.emailHelp")}
                            {...darkThemeStyles}
                        />

                        {/* Capítulo */}
                        <div className="mb-4">
                            <label className={darkThemeStyles.labelClassName}>
                                {t("sorteoForm.chapterLabel")}
                            </label>
                            <div className="justify-center flex gap-4 flex-wrap">
                                {[1, 2, 3, 4, 5].map((n) => (
                                    <label key={n} className="text-white">
                                        <input type="radio" value={`Capítulo ${n}`} {...register("capitulo", { required: true })} /> {t("sorteoForm.chapter")} {n}
                                    </label>
                                ))}
                            </div>
                            <div className="justify-center flex gap-4 flex-wrap mt-2">
                                {[6, 7, 8, 9].map((n) => (
                                    <label key={n} className="text-white">
                                        <input type="radio" value={`Capítulo ${n}`} {...register("capitulo", { required: true })} /> {t("sorteoForm.chapter")} {n}
                                    </label>
                                ))}
                            </div>
                            {errors.capitulo && <span className="text-red-400 text-sm">{t("sorteoForm.chapterError")}</span>}
                        </div>

                        {/* Sexo */}
                        <RadioGroup
                            label={t("sorteoForm.genderLabel")}
                            name="sexo"
                            options={genderOptions}
                            register={register}
                            error={errors.sexo}
                            errorMessage={t("sorteoForm.required")}
                            {...darkThemeStyles}
                        />

                        {/* Edad */}
                        <TextField
                            label={t("sorteoForm.ageLabel")}
                            name="edad"
                            type="number"
                            register={register}
                            validation={{ required: true, min: 1, max: 999 }}
                            value={edadValue}
                            onChange={handleEdadChange}
                            error={errors.edad}
                            errorMessage={t("sorteoForm.ageError")}
                            inputMode="numeric"
                            min={1}
                            max={999}
                            {...darkThemeStyles}
                        />

                        {/* Código Postal */}
                        <TextField
                            label={t("sorteoForm.postalCodeLabel")}
                            name="cp"
                            type="text"
                            register={register}
                            validation={{ required: true, pattern: /^[0-9]{5}$/ }}
                            value={cpValue}
                            onChange={handleCpChange}
                            error={errors.cp}
                            errorMessage={t("sorteoForm.postalCodeError")}
                            inputMode="numeric"
                            maxLength={5}
                            {...darkThemeStyles}
                        />

                        {/* Formato */}
                        <RadioGroup
                            label={t("sorteoForm.formatLabel")}
                            name="formato"
                            options={formatOptions}
                            register={register}
                            error={errors.formato}
                            errorMessage={t("sorteoForm.required")}
                            {...darkThemeStyles}
                        />

                        {/* Lector habitual */}
                        <ScaleField
                            label={t("sorteoForm.readerLabel")}
                            description={t("sorteoForm.readerDescription")}
                            name="lectorHabitual"
                            register={register}
                            error={errors.lectorHabitual}
                            errorMessage={t("sorteoForm.required")}
                            {...darkThemeStyles}
                        />

                        {/* Visitado lugar */}
                        <RadioGroup
                            label={t("sorteoForm.visitedLabel")}
                            name="visitadoLugar"
                            options={yesNoOptions}
                            register={register}
                            error={errors.visitadoLugar}
                            errorMessage={t("sorteoForm.required")}
                            {...darkThemeStyles}
                        />

                        {/* Conocía lugar */}
                        <RadioGroup
                            label={t("sorteoForm.knewPlaceLabel")}
                            name="conociaLugar"
                            options={yesNoOptions}
                            register={register}
                            error={errors.conociaLugar}
                            errorMessage={t("sorteoForm.required")}
                            {...darkThemeStyles}
                        />

                        {/* Interés visitar */}
                        <ScaleField
                            label={t("sorteoForm.visitInterestLabel")}
                            description={t("sorteoForm.visitInterestDescription")}
                            name="visitarLugar"
                            register={register}
                            error={errors.visitarLugar}
                            errorMessage={t("sorteoForm.required")}
                            {...darkThemeStyles}
                        />

                        {/* Grupo Emociones */}
                        <fieldset className="mb-4 border border-white/20 rounded-md p-4">
                            <legend className="text-lg font-semibold text-yellow-400">
                                {t("sorteoForm.emotionsLegend")}
                            </legend>

                            <ScaleField
                                label=""
                                description={t("sorteoForm.curiosityLabel")}
                                name="emocionCuriosidad"
                                register={register}
                                error={errors.emocionCuriosidad}
                                errorMessage={t("sorteoForm.required")}
                                {...darkThemeStyles}
                                className="mt-4"
                            />

                            <ScaleField
                                label=""
                                description={t("sorteoForm.prideLabel")}
                                name="emocionOrgullo"
                                register={register}
                                error={errors.emocionOrgullo}
                                errorMessage={t("sorteoForm.required")}
                                {...darkThemeStyles}
                                className="mt-4"
                            />

                            <ScaleField
                                label=""
                                description={t("sorteoForm.connectionLabel")}
                                name="emocionesMonumento"
                                register={register}
                                error={errors.emocionesMonumento}
                                errorMessage={t("sorteoForm.required")}
                                {...darkThemeStyles}
                                className="mt-4"
                            />
                        </fieldset>

                        {/* Tiempo lectura */}
                        <RadioGroup
                            label={t("sorteoForm.readingTimeLabel")}
                            name="tiempoLectura"
                            options={readingTimeOptions}
                            register={register}
                            error={errors.tiempoLectura}
                            errorMessage={t("sorteoForm.required")}
                            {...darkThemeStyles}
                        />

                        {/* Flashbacks */}
                        <RadioGroup
                            label={t("sorteoForm.flashbacksLabel")}
                            name="flashbacksEntendidos"
                            options={yesNoOptions}
                            register={register}
                            error={errors.flashbacksEntendidos}
                            errorMessage={t("sorteoForm.required")}
                            {...darkThemeStyles}
                        />

                        {/* Formato preferido */}
                        <RadioGroup
                            label={t("sorteoForm.preferredFormatLabel")}
                            name="formatoPreferido"
                            options={preferredFormatOptions}
                            register={register}
                            error={errors.formatoPreferido}
                            errorMessage={t("sorteoForm.required")}
                            {...darkThemeStyles}
                        />

                        {/* Interés aventuras */}
                        <ScaleField
                            label={t("sorteoForm.adventuresLabel")}
                            description={t("sorteoForm.adventuresDescription")}
                            name="interesAventuras"
                            register={register}
                            error={errors.interesAventuras}
                            errorMessage={t("sorteoForm.required")}
                            {...darkThemeStyles}
                        />

                        {/* Narrativa monumento */}
                        <ScaleField
                            label={t("sorteoForm.narrativeLabel")}
                            description={t("sorteoForm.narrativeDescription")}
                            name="narrativaMonumento"
                            register={register}
                            error={errors.narrativaMonumento}
                            errorMessage={t("sorteoForm.required")}
                            {...darkThemeStyles}
                        />

                        {/* Comentarios */}
                        <TextAreaField
                            label={t("sorteoForm.commentsLabel")}
                            name="comentarios"
                            register={register}
                            validation={{ maxLength: 500 }}
                            maxLength={500}
                            {...darkThemeStyles}
                        />

                        {/* GDPR */}
                        <CheckboxField
                            label={t("sorteoForm.gdprLabel")}
                            name="gdpr"
                            register={register}
                            error={errors.gdpr}
                            errorMessage={t("sorteoForm.gdprError")}
                            labelClassName="flex items-center text-white"
                        />

                        {/* Botones */}
                        <div className="flex gap-4 justify-center pt-4">
                            <NeonButton type="submit" disabled={isSubmitting}>
                                {isSubmitting ? t("sorteoForm.submitting") : t("sorteoForm.submitButton")}
                            </NeonButton>
                            <SecondaryButton onClick={onBack}>
                                {t("sorteoForm.backButton")}
                            </SecondaryButton>
                        </div>
                    </form>
                </div>
            </div>
        </motion.div>
    );
};

export default SorteoOpinionForm;
